// client/src/coms/components/StickerEditorModal.js
// Settings → Stickers dialogs: the sticker editor (preview + keyword chips, saved to the server on Save),
// the full-size preview, and the delete confirmation. Styled like TrustDialog.
import React, { useEffect, useRef, useState } from 'react';
import { comsApiPatch, comsApiPut } from '../comsApi';
import KeywordTagInput from './KeywordTagInput';

function errText(e, fallback) {
  return String((e && e.data && e.data.error) || (e && e.message) || fallback);
}

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select, textarea, a[href], [tabindex]:not([tabindex="-1"])';

// Backdrop + card. Esc and a click on the backdrop close it; Tab stays inside; focus returns to the opener.
export function ModalShell({ label, onClose, children, width = 480, zIndex = 1100, plain = false }) {
  const cardRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Captured on the first render, before autoFocus inside the dialog moves focus.
  const [opener] = useState(() => (typeof document !== 'undefined' ? document.activeElement : null));

  useEffect(() => {
    const card = cardRef.current;
    if (card && !card.contains(document.activeElement)) {
      const first = card.querySelector('[autofocus], input:not([disabled])') || card.querySelector(FOCUSABLE);
      if (first) first.focus();
    }
    return () => {
      try {
        if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
      } catch {}
    };
  }, [opener]);

  function onKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCloseRef.current();
      return;
    }
    if (e.key !== 'Tab' || !cardRef.current) return;
    const items = Array.from(cardRef.current.querySelectorAll(FOCUSABLE));
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCloseRef.current();
      }}
      onKeyDown={onKeyDown}
      style={{ position: 'fixed', inset: 0, background: plain ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex, padding: 12 }}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        style={
          plain
            ? { maxWidth: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, color: 'var(--vx-text, #fff)' }
            : {
                width: `min(${width}px, 100%)`,
                maxHeight: 'calc(100vh - 24px)',
                overflowY: 'auto',
                boxSizing: 'border-box',
                background: 'var(--vxs-3, #1b1c1f)',
                border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                borderRadius: 14,
                padding: 18,
                color: 'var(--vx-text, #fff)',
              }
        }
      >
        {children}
      </div>
    </div>
  );
}

const closeX = { marginLeft: 'auto', border: 0, background: 'transparent', color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.7 * var(--vx-ov, 1)))', cursor: 'pointer', fontSize: 16, padding: '2px 6px', borderRadius: 6 };

// sticker: { id, name, url, keywords }. pack: { name, can_edit }.
// onSaved({ keywords, name }) after the server stored the changes. onMove(-1 | 1) reorders the sticker.
// persist({ name, keywords }): saves somewhere else instead of the server (local stickers on this device).
export default function StickerEditorModal({ sticker, pack, onClose, onSaved, onPreview, onMove, canMoveEarlier = false, canMoveLater = false, persist = null }) {
  const canEdit = !!(pack && pack.can_edit);
  const original = sticker.keywords || [];
  const [keywords, setKeywords] = useState(original);
  const [name, setName] = useState(sticker.name || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const tagRef = useRef(null);

  async function save() {
    const next = tagRef.current ? tagRef.current.commit() : keywords;
    const nextName = name.trim() || sticker.name;
    const keywordsChanged = next.join('\n') !== original.join('\n');
    const nameChanged = nextName !== sticker.name;
    if (!keywordsChanged && !nameChanged) {
      onClose();
      return;
    }
    setSaving(true);
    setErr('');
    try {
      if (typeof persist === 'function') {
        await persist({ name: nextName, keywords: next });
        onSaved({ keywords: next, name: nextName });
        return;
      }
      if (nameChanged) await comsApiPatch(`/stickers/stickers/${sticker.id}`, { name: nextName });
      const r = keywordsChanged ? await comsApiPut(`/stickers/stickers/${sticker.id}/keywords`, { keywords: next }) : null;
      onSaved({ keywords: (r && r.keywords) || next, name: nextName });
    } catch (e) {
      setErr(errText(e, 'Could not save the keywords.'));
      setSaving(false);
    }
  }

  return (
    <ModalShell label={`Edit sticker ${sticker.name}`} onClose={() => !saving && onClose()}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 17, fontWeight: 900 }}>{canEdit ? 'Edit sticker' : 'Sticker keywords'}</div>
        <button type="button" aria-label="Close" onClick={onClose} disabled={saving} style={closeX}>
          ✕
        </button>
      </div>

      <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
        <div style={{ width: 'min(160px, 40vw)', aspectRatio: '1', borderRadius: 12, background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
          <img src={sticker.url} alt={sticker.name} style={{ maxWidth: '92%', maxHeight: '92%', objectFit: 'contain' }} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          {canEdit ? (
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={40}
              aria-label="Sticker name"
              disabled={saving}
              style={{ background: 'var(--vxs-1, #111214)', color: 'var(--vx-text, #fff)', border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))', borderRadius: 8, padding: '6px 8px', fontWeight: 800, fontSize: 14, width: '100%', boxSizing: 'border-box' }}
            />
          ) : (
            <div style={{ fontWeight: 800, fontSize: 15, overflowWrap: 'anywhere' }}>{sticker.name}</div>
          )}
          <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>{pack ? pack.name : ''} · {sticker.local ? 'stored on this device' : `sticker ID ${sticker.id}`}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {typeof onPreview === 'function' ? (
              <button type="button" className="coms-mini-btn tiny" onClick={onPreview}>
                🔍 View full size
              </button>
            ) : null}
            {canEdit && typeof onMove === 'function' ? (
              <>
                <button type="button" className="coms-mini-btn tiny" disabled={!canMoveEarlier || saving} onClick={() => onMove(-1)} aria-label="Move sticker earlier">
                  ← Earlier
                </button>
                <button type="button" className="coms-mini-btn tiny" disabled={!canMoveLater || saving} onClick={() => onMove(1)} aria-label="Move sticker later">
                  Later →
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 2 }}>Keywords</div>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8, lineHeight: 1.4 }}>
          {canEdit
            ? 'Typing one of these words in a message suggests this sticker. Keywords are single words and are not case-sensitive.'
            : 'Only the pack’s owner can change these keywords.'}
        </div>
        {canEdit || keywords.length ? (
          <KeywordTagInput ref={tagRef} label={`Keywords for ${sticker.name}`} value={keywords} onChange={setKeywords} disabled={!canEdit || saving} autoFocus={canEdit} />
        ) : (
          <div style={{ fontSize: 12, opacity: 0.6 }}>No keywords.</div>
        )}
      </div>

      {err ? (
        <div className="coms-form-error" role="alert" style={{ marginTop: 10 }}>
          {err}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        {canEdit ? (
          <>
            <button type="button" className="coms-mini-btn" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="coms-mini-btn primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </>
        ) : (
          <button type="button" className="coms-mini-btn" onClick={onClose}>
            Close
          </button>
        )}
      </div>
    </ModalShell>
  );
}

// Full-size view of a sticker (its stored resolution, scaled down only to fit the window).
export function StickerPreview({ sticker, onClose }) {
  return (
    <ModalShell label={`${sticker.name} full size`} onClose={onClose} zIndex={1200} plain>
      <img src={sticker.url} alt={sticker.name} style={{ maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 110px)', objectFit: 'contain', imageRendering: 'auto' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
        <span style={{ fontWeight: 800 }}>{sticker.name}</span>
        <button type="button" className="coms-mini-btn" onClick={onClose} autoFocus>
          Close
        </button>
      </div>
    </ModalShell>
  );
}

export function ConfirmDialog({ title, message, confirmLabel = 'Delete', busy = false, onConfirm, onCancel }) {
  return (
    <ModalShell label={title} onClose={() => !busy && onCancel()} width={380}>
      <div style={{ fontSize: 16, fontWeight: 900 }}>{title}</div>
      <div style={{ fontSize: 13, opacity: 0.8, marginTop: 8, lineHeight: 1.45 }}>{message}</div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" className="coms-mini-btn" onClick={onCancel} disabled={busy} autoFocus>
          Cancel
        </button>
        <button type="button" className="coms-mini-btn" onClick={onConfirm} disabled={busy} style={{ background: 'rgba(220,38,38,0.85)', borderColor: 'rgba(220,38,38,0.85)' }}>
          {busy ? 'Deleting…' : confirmLabel}
        </button>
      </div>
    </ModalShell>
  );
}
